import type {
  AllergyIntolerance,
  AllergyIntoleranceReaction,
  CodeableConcept,
  Patient,
} from '@medplum/fhirtypes'
import { z } from 'zod'
import {
  ALLERGY_UNKNOWN_SYSTEM,
  EXTERNAL_RESOURCE_JSON_SYSTEM,
  HL7_ALLERGY_INTOLERANCE_LINK,
} from '#src/codeSystems.ts'
import { elationSandboxIdentifier } from '#src/elation.ts'
import { createReference } from '#src/references.ts'
import { build } from '#src/utils.ts'

interface SeverityByCode {
  [severity: string]: AllergyIntoleranceReaction['severity']
}

const severityMap: SeverityByCode = {
  '255604002': 'mild',
  '6736007': 'moderate',
  '24484000': 'severe',
} as const

export const drugIntoleranceSchema = z.object({
  id: z.number(),
  status: z
    .number()
    .refine((val) => val === 0 || val === 1, 'Status must be 0 or 1'),
  start_date: z.string().optional().nullable(),
  reaction: z.string(),
  name: z.string(),
  severity: z.enum(['255604002', '6736007', '24484000']).optional().nullable(),
  patient: z.number(),
  created_date: z.string().optional().nullable(),
  deleted_date: z.string().optional().nullable(),
})

export type DrugIntolerance = z.infer<typeof drugIntoleranceSchema>

interface ClinicalStatusByCode {
  [status: number]: CodeableConcept
}

const clinicalStatusMap: ClinicalStatusByCode = {
  1: {
    coding: [
      {
        system: HL7_ALLERGY_INTOLERANCE_LINK,
        code: 'active',
        display: 'Active',
      },
    ],
  },
  0: {
    coding: [
      {
        system: HL7_ALLERGY_INTOLERANCE_LINK,
        code: 'inactive',
        display: 'Inactive',
      },
    ],
  },
}

export function fromFhir(
  _drugIntolerance: AllergyIntolerance
): DrugIntolerance {
  // We don't currently have a use case for converting a FHIR AllergyIntolerance to an Elation Drug Intolerance
  // as we only sync uni-directionally from Elation
  throw new Error('Not implemented')
}

export function toFhir(
  drugIntolerance: DrugIntolerance,
  patient: Patient
): AllergyIntolerance {
  let clinicalStatus = clinicalStatusMap[drugIntolerance.status]
  if (drugIntolerance.deleted_date) {
    clinicalStatus = clinicalStatusMap[0]
  }
  const severity = drugIntolerance.severity
    ? severityMap[drugIntolerance.severity]
    : undefined

  return build('AllergyIntolerance', {
    identifier: [elationSandboxIdentifier(drugIntolerance.id.toString())],
    patient: createReference(patient),
    clinicalStatus,
    code: {
      coding: [
        {
          // elation does not provide codes for drug intolerances
          system: ALLERGY_UNKNOWN_SYSTEM,
          display: drugIntolerance.name,
        },
      ],
    },
    category: ['medication'],
    type: 'intolerance',
    reaction: [
      {
        manifestation: [
          {
            text: drugIntolerance.reaction,
          },
        ],
        severity,
      },
    ],
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml">${drugIntolerance.name}</div>`,
    },
    extension: [
      {
        url: EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(drugIntolerance),
      },
    ],
  })
}
